import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon,
  CalendarIcon,
  MapPinIcon,
  UsersIcon,
  DocumentTextIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Trip, Booking } from '../../types';
import TripForm from './TripForm';
import TripDetails from './TripDetails';
import TripPassengerManifest from '../../components/TripPassengerManifest';
import { DateInput, DateDisplay } from '../../components/Forms';

interface TripFilters {
  search: string;
  status: string;
  startDate: string;
  endDate: string;
}

const Trips: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<TripFilters>({
    search: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showTripForm, setShowTripForm] = useState(false);
  const [showTripDetails, setShowTripDetails] = useState(false);
  const [showPassengerManifest, setShowPassengerManifest] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [manifestTrip, setManifestTrip] = useState<Trip | null>(null);
  const [manifestBookings, setManifestBookings] = useState<Booking[]>([]);

  const queryClient = useQueryClient();

  // Fetch trips with React Query
  const { 
    data: tripsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['trips', currentPage, filters],
    queryFn: () => apiService.getTrips(currentPage, 20, filters),
    staleTime: 30000, // 30 seconds
  });

  const trips = tripsData?.data || [];
  const pagination = tripsData?.pagination;

  // Delete trip mutation
  const deleteTripMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteTrip(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof TripFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteTrip = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الرحلة؟')) {
      try {
        await deleteTripMutation.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting trip:', error);
        alert('حدث خطأ أثناء حذف الرحلة');
      }
    }
  };

  const handleCreateTrip = () => {
    setSelectedTrip(null);
    setShowTripForm(true);
  };

  const handleEditTrip = (trip: Trip) => {
    setSelectedTrip(trip);
    setShowTripForm(true);
  };

  const handleViewTrip = (tripId: string) => {
    setSelectedTripId(tripId);
    setShowTripDetails(true);
  };

  const handleTripFormClose = () => {
    setShowTripForm(false);
    setSelectedTrip(null);
  };

  const handleTripDetailsClose = () => {
    setShowTripDetails(false);
    setSelectedTripId(null);
  };

  const handleTripSuccess = (trip: Trip) => {
    console.log('Trip saved successfully:', trip);
  };

  const handleShowPassengerManifest = async (trip: Trip) => {
    try {
      // Fetch bookings for this trip with reasonable limit
      const bookingsResponse = await apiService.getBookings(1, 100, { tripId: trip._id });
      setManifestTrip(trip);
      setManifestBookings(bookingsResponse.data || []);
      setShowPassengerManifest(true);
    } catch (error) {
      console.error('Error fetching trip bookings:', error);
      alert('حدث خطأ أثناء تحميل بيانات المسافرين');
    }
  };

  const handlePassengerManifestClose = () => {
    setShowPassengerManifest(false);
    setManifestTrip(null);
    setManifestBookings([]);
  };

  const handlePrintManifest = () => {
    window.print();
  };

  const formatTime = (date: string | Date) => {
    return new Date(date).toLocaleTimeString('ar-SA-u-nu-latn', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get trip route info
  const getTripRouteInfo = (trip: Trip) => {
    if (trip.routes && trip.routes.length > 0) {
      const activeRoute = trip.routes.find(r => r.isActive) || trip.routes[0];
      return {
        from: activeRoute.from,
        to: activeRoute.to,
        departureDate: activeRoute.dateFrom || trip.date,
        departureTime: activeRoute.timeFrom,
        arrivalTime: activeRoute.timeTo,
      };
    }

    if (trip.route) {
      return {
        from: trip.route.from,
        to: trip.route.to,
        departureDate: trip.route.departureTime || trip.date,
        departureTime: trip.route.departureTime
          ? new Date(trip.route.departureTime).toISOString().slice(11, 16)
          : '',
        arrivalTime: trip.route.arrivalTime
          ? new Date(trip.route.arrivalTime).toISOString().slice(11, 16)
          : '',
      };
    }

    return {
      from: 'غير محدد',
      to: 'غير محدد',
      departureDate: trip.date,
      departureTime: '',
      arrivalTime: '',
    };
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'draft': { label: 'مسودة', color: 'bg-gray-100 text-gray-800' },
      'published': { label: 'منشورة', color: 'bg-blue-100 text-blue-800' },
      'full': { label: 'مكتملة العدد', color: 'bg-yellow-100 text-yellow-800' },
      'ongoing': { label: 'جارية', color: 'bg-green-100 text-green-800' },
      'completed': { label: 'مكتملة', color: 'bg-purple-100 text-purple-800' },
      'cancelled': { label: 'ملغية', color: 'bg-red-100 text-red-800' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">خطأ في تحميل البيانات</h3>
          <p className="text-red-600 mt-1">حدث خطأ أثناء تحميل بيانات الرحلات</p>
          <button 
            onClick={() => refetch()}
            className="mt-3 bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة الرحلات</h1>
          <p className="mt-1 text-sm text-gray-500">
            إجمالي الرحلات: {pagination?.total || 0}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button onClick={handleCreateTrip} className="btn-primary">
            <PlusIcon className="w-5 h-5 ml-2" />
            رحلة جديدة
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم الرحلة، الوجهة، أو رقم الباص..."
                className="input-field pr-10"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="w-5 h-5 ml-2" />
            فلترة
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  حالة الرحلة
                </label>
                <select
                  className="input-field"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">جميع الحالات</option>
                  <option value="draft">مسودة</option>
                  <option value="published">منشورة</option>
                  <option value="full">مكتملة العدد</option>
                  <option value="ongoing">جارية</option>
                  <option value="completed">مكتملة</option>
                  <option value="cancelled">ملغية</option>
                </select>
              </div>

              <div>
                <DateInput
                  label="من تاريخ"
                  compactToggle
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>

              <div>
                <DateInput
                  label="إلى تاريخ"
                  compactToggle
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  min={filters.startDate || undefined}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trips Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">جاري تحميل الرحلات...</p>
          </div>
        ) : trips.length === 0 ? (
          <div className="p-8 text-center">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-4 text-gray-500">لا توجد رحلات</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      رقم الرحلة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      التاريخ والوقت
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      المسار
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الحافلة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      السائق
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      المسافرين
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الحالة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trips.map((trip) => {
                    const routeInfo = getTripRouteInfo(trip);
                    return (
                    <tr key={trip._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {trip.tripNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DateDisplay
                          value={routeInfo.departureDate || trip.date}
                          gregorianClassName="text-sm font-semibold text-gray-900 leading-tight"
                          hijriClassName="text-xs font-medium text-gray-500 leading-tight"
                        />
                        {(routeInfo.departureTime || routeInfo.arrivalTime) && (
                          <div className="text-sm text-gray-500 mt-0.5">
                            {routeInfo.departureTime || '—'} - {routeInfo.arrivalTime || '—'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <MapPinIcon className="w-4 h-4 ml-1 text-gray-400" />
                          {routeInfo.from} ← {routeInfo.to}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {trip.busCount || trip.allBuses?.length || trip.buses?.length || 1} حافلة
                        </div>
                        <div className="text-sm text-gray-500">
                          {trip.busCount && trip.busCount > 1 
                            ? `${trip.allBuses?.[0]?.bus?.busNumber || trip.buses?.[0]?.bus?.busNumber || '-'} + ${trip.busCount - 1} أخرى`
                            : trip.bus?.busNumber || trip.buses?.[0]?.bus?.busNumber || '-'
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {trip.driver?.name || trip.buses?.[0]?.driver?.name || '-'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {trip.driver?.phone || trip.buses?.[0]?.driver?.phone || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <UsersIcon className="w-4 h-4 ml-1 text-gray-400" />
                          {trip.totalPassengers || 0} / {trip.bus?.capacity || trip.buses?.[0]?.bus?.capacity || 0}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ 
                              width: `${Math.min(100, ((trip.totalPassengers || 0) / (trip.bus?.capacity || trip.buses?.[0]?.bus?.capacity || 1)) * 100)}%` 
                            }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(trip.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            title="عرض التفاصيل"
                            onClick={() => handleViewTrip(trip._id)}
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button
                            className="text-purple-600 hover:text-purple-900"
                            title="كشف المسافرين"
                            onClick={() => handleShowPassengerManifest(trip)}
                          >
                            <DocumentTextIcon className="w-4 h-4" />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900"
                            title="تعديل"
                            onClick={() => handleEditTrip(trip)}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            title="حذف"
                            onClick={() => handleDeleteTrip(trip._id)}
                            disabled={deleteTripMutation.isPending}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      السابق
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.pages, currentPage + 1))}
                      disabled={currentPage === pagination.pages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      التالي
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        عرض{' '}
                        <span className="font-medium">
                          {((currentPage - 1) * 20) + 1}
                        </span>{' '}
                        إلى{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * 20, pagination.total)}
                        </span>{' '}
                        من{' '}
                        <span className="font-medium">{pagination.total}</span>{' '}
                        نتيجة
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          السابق
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(Math.min(pagination.pages, currentPage + 1))}
                          disabled={currentPage === pagination.pages}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          التالي
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Trip Form Modal */}
      <TripForm
        trip={selectedTrip || undefined}
        isOpen={showTripForm}
        onClose={handleTripFormClose}
        onSuccess={handleTripSuccess}
      />

      {/* Trip Details Modal */}
      {selectedTripId && (
        <TripDetails
          tripId={selectedTripId}
          isOpen={showTripDetails}
          onClose={handleTripDetailsClose}
          onEdit={handleEditTrip}
        />
      )}

      {/* Passenger Manifest Modal */}
      {showPassengerManifest && manifestTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-blue-600 text-white p-4 flex justify-between items-center no-print">
              <h2 className="text-lg font-bold">كشف مبدئي للمسافرين - رحلة {manifestTrip.tripNumber}</h2>
              <div className="flex items-center space-x-2 space-x-reverse">
                <button
                  onClick={handlePrintManifest}
                  className="bg-white text-blue-600 px-3 py-1 rounded hover:bg-gray-100 flex items-center"
                >
                  <PrinterIcon className="w-4 h-4 ml-1" />
                  طباعة
                </button>
                <button
                  onClick={handlePassengerManifestClose}
                  className="text-white hover:text-gray-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              <TripPassengerManifest
                trip={manifestTrip}
                bookings={manifestBookings}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trips; 